import React, { useMemo, useState, useRef } from "react";
import { Box, Typography, Chip, Popover, Divider, Link } from "@mui/material";
import {
    format,
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    addDays,
    isSameMonth,
    isSameDay,
    isToday
} from "date-fns";

const MAX_VISIBLE_EVENTS = 3;

function BookingPill({ bk }) {
    return (
        <Box
            sx={{
                backgroundColor:
                    STATUS_COLORS[bk.status] || STATUS_COLORS.CONFIRMED,
                color: "#fff",
                borderRadius: "3px",
                px: 0.5,
                mb: "2px",
                fontSize: "0.6rem",
                lineHeight: 1.4,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
            }}
        >
            {bk.startTime}{" "}
            {bk.clientName || bk.servicetypeName || "Booking"}
        </Box>
    );
}

function formatTime12h(time24) {
    if (!time24) return "";
    const [h, m] = time24.split(":").map(Number);
    if (Number.isNaN(h)) return time24;
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_COLORS = {
    CONFIRMED: "#0087EE",
    PENDING: "#90CAF9",
    SCHEDULED: "#0087EE"
};
const BLOCKED_COLOR = "#9E9E9E";

/**
 * CalendarMonthGrid - Month view showing days with booking/event previews.
 * Clicking a day switches to Day view for that date.
 */
export default function CalendarMonthGrid({
    calendarData,
    providers,
    anchorDate,
    onDayClick
}) {
    const [moreAnchor, setMoreAnchor] = useState(null);
    const [moreDay, setMoreDay] = useState(null);
    const [morePinned, setMorePinned] = useState(false);
    const closeTimeoutRef = useRef(null);
    const pinnedRef = useRef(false);

    const cancelScheduledClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    const scheduleClose = () => {
        cancelScheduledClose();
        closeTimeoutRef.current = setTimeout(() => {
            // Don't close if the user has pinned the popover by clicking
            if (pinnedRef.current) return;
            setMoreAnchor(null);
            setMoreDay(null);
        }, 180);
    };

    const handleMoreEnter = (e, day) => {
        cancelScheduledClose();
        // If already pinned to a different day, don't override on hover
        if (pinnedRef.current) return;
        setMoreAnchor(e.currentTarget);
        setMoreDay(day);
    };

    const handleMoreLeave = () => {
        if (pinnedRef.current) return;
        scheduleClose();
    };

    const handleMoreClick = (e, day) => {
        e.stopPropagation();
        cancelScheduledClose();
        setMoreAnchor(e.currentTarget);
        setMoreDay(day);
        pinnedRef.current = true;
        setMorePinned(true);
    };

    const handleMoreClose = () => {
        cancelScheduledClose();
        pinnedRef.current = false;
        setMorePinned(false);
        setMoreAnchor(null);
        setMoreDay(null);
    };
    // Build the 6-week grid of days
    const weeks = useMemo(() => {
        const monthStart = startOfMonth(anchorDate);
        const monthEnd = endOfMonth(anchorDate);
        const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

        const result = [];
        let current = gridStart;
        let week = [];

        while (current <= gridEnd) {
            week.push(new Date(current));
            if (week.length === 7) {
                result.push(week);
                week = [];
            }
            current = addDays(current, 1);
        }
        return result;
    }, [anchorDate]);

    // Group all events by date across all providers
    const eventsByDate = useMemo(() => {
        const map = {};
        if (!calendarData?.providers) return map;

        for (const provider of providers) {
            const providerData = calendarData.providers[provider.id];
            if (!providerData) continue;

            for (const bk of providerData.bookings || []) {
                const dateKey = (bk.date || bk.startDate || "").slice(0, 10);
                if (!dateKey) continue;
                if (!map[dateKey]) map[dateKey] = { bookings: [], blockedTimes: [] };
                map[dateKey].bookings.push({
                    ...bk,
                    providerName: `${provider.firstname || ""} ${provider.lastname || ""}`.trim()
                });
            }

            for (const bt of providerData.blockedTimes || []) {
                const dateKey = (bt.date || "").slice(0, 10);
                if (!dateKey) continue;
                if (!map[dateKey]) map[dateKey] = { bookings: [], blockedTimes: [] };
                map[dateKey].blockedTimes.push(bt);
            }
        }
        return map;
    }, [calendarData, providers]);

    // Check availability for a day
    const availabilityByDate = useMemo(() => {
        const map = {};
        if (!calendarData?.providers) return map;

        for (const provider of providers) {
            const providerData = calendarData.providers[provider.id];
            if (!providerData) continue;

            for (const slot of providerData.availability || []) {
                if (!slot.date) continue;
                map[slot.date] = true;
            }
        }
        return map;
    }, [calendarData, providers]);

    return (
        <Box sx={{ flex: 1, overflow: "auto", p: 1 }}>
            {/* Day headers */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    mb: 0.5
                }}
            >
                {DAY_NAMES.map((day) => (
                    <Box
                        key={day}
                        sx={{
                            textAlign: "center",
                            py: 0.5,
                            fontWeight: 600,
                            color: "text.secondary"
                        }}
                    >
                        <Typography variant="caption">{day}</Typography>
                    </Box>
                ))}
            </Box>

            {/* Day-detail popover ("+X more" / blocked-count click) */}
            {(() => {
                if (!moreDay) return null;
                const dateStr = format(moreDay, "yyyy-MM-dd");
                const dayEvents = eventsByDate[dateStr] || {
                    bookings: [],
                    blockedTimes: []
                };
                const sortedBookings = [...dayEvents.bookings].sort((a, b) =>
                    (a.startTime || "").localeCompare(b.startTime || "")
                );
                const sortedBlocked = [...dayEvents.blockedTimes].sort((a, b) =>
                    (a.startTime || "").localeCompare(b.startTime || "")
                );
                return (
                    <Popover
                        open={Boolean(moreAnchor)}
                        anchorEl={moreAnchor}
                        onClose={handleMoreClose}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left"
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "left"
                        }}
                        disableRestoreFocus
                        {...(!morePinned && {
                            disableScrollLock: true,
                            slotProps: {
                                root: {
                                    sx: { pointerEvents: "none" }
                                },
                                paper: {
                                    sx: {
                                        width: 280,
                                        maxHeight: 400,
                                        p: 0,
                                        borderRadius: 2,
                                        boxShadow:
                                            "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
                                        pointerEvents: "auto"
                                    },
                                    onMouseEnter: cancelScheduledClose,
                                    onMouseLeave: scheduleClose
                                }
                            }
                        })}
                        {...(morePinned && {
                            slotProps: {
                                paper: {
                                    sx: {
                                        width: 280,
                                        maxHeight: 400,
                                        p: 0,
                                        borderRadius: 2,
                                        boxShadow:
                                            "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
                                    }
                                }
                            }
                        })}
                    >
                        <Box
                            sx={{
                                px: 2,
                                py: 1.25,
                                borderBottom: "1px solid #f0f0f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between"
                            }}
                        >
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                            >
                                {format(moreDay, "EEE, MMM d")}
                            </Typography>
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => {
                                    const target = moreDay;
                                    handleMoreClose();
                                    onDayClick?.(target);
                                }}
                                sx={{ fontSize: "0.75rem" }}
                            >
                                Open day
                            </Link>
                        </Box>
                        <Box sx={{ p: 1.25, overflowY: "auto", maxHeight: 340 }}>
                            {sortedBookings.length === 0 &&
                                sortedBlocked.length === 0 && (
                                    <Typography
                                        variant="body2"
                                        color="text.disabled"
                                        sx={{
                                            fontStyle: "italic",
                                            textAlign: "center",
                                            py: 1
                                        }}
                                    >
                                        No events
                                    </Typography>
                                )}
                            {sortedBookings.map((bk, idx) => (
                                <Box
                                    key={`bk-${idx}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        py: 0.5,
                                        px: 0.5,
                                        borderRadius: 1,
                                        "&:hover": {
                                            backgroundColor:
                                                "rgba(0,135,238,0.06)"
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 4,
                                            alignSelf: "stretch",
                                            borderRadius: 1,
                                            backgroundColor:
                                                STATUS_COLORS[bk.status] ||
                                                STATUS_COLORS.CONFIRMED,
                                            flexShrink: 0
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: "0.8rem",
                                                lineHeight: 1.3
                                            }}
                                            noWrap
                                        >
                                            {bk.clientName ||
                                                bk.servicetypeName ||
                                                "Booking"}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: "block",
                                                fontSize: "0.7rem"
                                            }}
                                        >
                                            {formatTime12h(bk.startTime)}
                                            {bk.endTime
                                                ? ` – ${formatTime12h(bk.endTime)}`
                                                : ""}
                                            {bk.servicetypeName &&
                                            bk.clientName
                                                ? ` · ${bk.servicetypeName}`
                                                : ""}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                            {sortedBookings.length > 0 &&
                                sortedBlocked.length > 0 && (
                                    <Divider sx={{ my: 0.75 }} />
                                )}
                            {sortedBlocked.map((bt, idx) => (
                                <Box
                                    key={`bt-${idx}`}
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        py: 0.5,
                                        px: 0.5,
                                        borderRadius: 1,
                                        "&:hover": {
                                            backgroundColor: "rgba(0,0,0,0.03)"
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 4,
                                            alignSelf: "stretch",
                                            borderRadius: 1,
                                            backgroundColor: BLOCKED_COLOR,
                                            flexShrink: 0
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                fontSize: "0.8rem",
                                                lineHeight: 1.3
                                            }}
                                        >
                                            Blocked Time
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: "block",
                                                fontSize: "0.7rem"
                                            }}
                                        >
                                            {formatTime12h(bt.startTime)}
                                            {bt.endTime
                                                ? ` – ${formatTime12h(bt.endTime)}`
                                                : ""}
                                        </Typography>
                                        {bt.notes && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    display: "block",
                                                    fontSize: "0.7rem",
                                                    fontStyle: "italic",
                                                    whiteSpace: "pre-line",
                                                    mt: 0.25
                                                }}
                                            >
                                                {bt.notes}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Popover>
                );
            })()}

            {/* Week rows */}
            {weeks.map((week, wIdx) => (
                <Box
                    key={wIdx}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(7, 1fr)",
                        minHeight: 100
                    }}
                >
                    {week.map((day) => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const inMonth = isSameMonth(day, anchorDate);
                        const today = isToday(day);
                        const events = eventsByDate[dateStr];
                        const hasAvailability = availabilityByDate[dateStr];
                        const bookingCount = events?.bookings?.length || 0;
                        const blockedCount = events?.blockedTimes?.length || 0;

                        return (
                            <Box
                                key={dateStr}
                                onClick={() => onDayClick?.(day)}
                                sx={{
                                    border: "1px solid #f0f0f0",
                                    p: 0.5,
                                    cursor: "pointer",
                                    backgroundColor: !inMonth
                                        ? "#fafafa"
                                        : hasAvailability
                                          ? "#fff"
                                          : "rgba(0,0,0,0.02)",
                                    "&:hover": {
                                        backgroundColor: "rgba(0,135,238,0.05)"
                                    },
                                    minHeight: 90,
                                    position: "relative"
                                }}
                            >
                                {/* Day number */}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: today ? 700 : 400,
                                        color: !inMonth
                                            ? "text.disabled"
                                            : today
                                              ? "primary.main"
                                              : "text.primary",
                                        mb: 0.5,
                                        ...(today && {
                                            backgroundColor: "primary.main",
                                            color: "#fff",
                                            borderRadius: "50%",
                                            width: 24,
                                            height: 24,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "0.75rem"
                                        })
                                    }}
                                >
                                    {format(day, "d")}
                                </Typography>

                                {/* Event previews */}
                                {events?.bookings
                                    ?.slice(0, MAX_VISIBLE_EVENTS)
                                    .map((bk, idx) => (
                                        <BookingPill key={idx} bk={bk} />
                                    ))}

                                {bookingCount > MAX_VISIBLE_EVENTS && (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={(e) => handleMoreClick(e, day)}
                                        onMouseEnter={(e) =>
                                            handleMoreEnter(e, day)
                                        }
                                        onMouseLeave={handleMoreLeave}
                                        sx={{
                                            background: "none",
                                            border: "none",
                                            p: 0,
                                            mt: "1px",
                                            cursor: "pointer",
                                            color: "primary.main",
                                            fontWeight: 600,
                                            fontSize: "0.6rem",
                                            textAlign: "left",
                                            display: "block",
                                            "&:hover": {
                                                textDecoration: "underline"
                                            }
                                        }}
                                    >
                                        +{bookingCount - MAX_VISIBLE_EVENTS} more
                                    </Box>
                                )}

                                {blockedCount > 0 && (
                                    <Chip
                                        label={`${blockedCount} blocked`}
                                        size="small"
                                        onClick={(e) => handleMoreClick(e, day)}
                                        onMouseEnter={(e) =>
                                            handleMoreEnter(e, day)
                                        }
                                        onMouseLeave={handleMoreLeave}
                                        sx={{
                                            height: 16,
                                            fontSize: "0.55rem",
                                            backgroundColor: BLOCKED_COLOR,
                                            color: "#fff",
                                            mt: "2px",
                                            cursor: "pointer"
                                        }}
                                    />
                                )}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}
